import { DeleteOutlineRounded, EditRounded, InsertEmoticon } from '@mui/icons-material';
import React, { useState } from 'react';
import ConfirmDialog from '../../../../ConfirmDialog/ConfirmDialog';
import DropdownMenu from '../../../../shared/DropdownMenu';

import './ItemOptionsMenu.scss';

type ItemOptionsMenuProps = {
  itemName: string;
  isMobile: boolean;
  anchorEl: HTMLDivElement | null;
  setAnchorEl: (value: HTMLDivElement | null) => void;
  onRename: () => void;
  onEmojiEdit: ((event: React.MouseEvent<HTMLLIElement>) => void) | undefined;
  onDelete: () => void;
  deleteMessage: React.ReactNode;
};

/**
 * Options menu for a sidebar list or divider.
 *
 * The anchor is owned by the parent row rather than this component, because the
 * row also needs to know whether its menu is open.
 */
function ItemOptionsMenu({
  isMobile,
  anchorEl,
  setAnchorEl,
  onRename,
  onEmojiEdit,
  onDelete,
  deleteMessage,
}: ItemOptionsMenuProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleClose = () => setAnchorEl(null);

  const handleDeleteClose = () => {
    setShowDeleteDialog(false);
    handleClose();
  };

  const handleDeleteConfirm = () => {
    handleDeleteClose();
    onDelete();
  };

  return (
    <>
      <DropdownMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isMobile ? 'right' : 'left' }}
      >
        <DropdownMenu.Item
          icon={<EditRounded fontSize='small' />}
          deferUntilClosed
          onSelect={(event) => {
            event.stopPropagation();
            onRename();
          }}
        >
          Rename
        </DropdownMenu.Item>
        <DropdownMenu.Item
          icon={<InsertEmoticon fontSize='small' />}
          hidden={!(isMobile && onEmojiEdit)}
          onSelect={(event) => onEmojiEdit?.(event)}
        >
          Edit Emoji
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item
          tone='danger'
          icon={<DeleteOutlineRounded fontSize='small' />}
          keepOpen
          onSelect={(event) => {
            event.stopPropagation();
            setShowDeleteDialog(true);
          }}
        >
          Delete
        </DropdownMenu.Item>
      </DropdownMenu>

      <ConfirmDialog
        open={showDeleteDialog}
        title='Are you sure?'
        confirmLabel='Delete'
        onCancel={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        destructive
      >
        {deleteMessage}
      </ConfirmDialog>
    </>
  );
}

ItemOptionsMenu.displayName = 'ItemOptionsMenu';
export default ItemOptionsMenu;
